import { useEffect, useRef } from 'react';
import {
  CarpetCleaningIcon,
  DeckPatioIcon,
  GutterCleaningIcon,
  HvacIcon,
  LawnCareIcon,
  MovingIcon,
  PaintingIcon,
  PestControlIcon,
  PetcareIcon,
  PlumbingIcon,
  PoolCleaningIcon,
  RoofingIcon,
} from '../IndustryIcons';
import './IndustriesSection.css';
import IPHONE_SCREEN_SRC from '../../assets/mockup_inside.png';

const IPHONE_FRAME_SRC =
  'https://cdn.prod.website-files.com/68d51352c56b9318a4bc0671/68d51354c56b9318a4bc08e6_iPhone14-space-black-min.svg';

const IPHONE_SCREEN_SRCSET = `${IPHONE_SCREEN_SRC.replace('.png', '-p-500.png')} 500w, ${IPHONE_SCREEN_SRC} 573w`;

type IndustryColor = 'blue' | 'yellow' | 'brand';
type IndustryIconKey =
  | 'hvac'
  | 'roofing'
  | 'plumbing'
  | 'pest'
  | 'gutter'
  | 'painting'
  | 'carpet'
  | 'lawn'
  | 'pool'
  | 'petcare'
  | 'moving'
  | 'deck';

type Industry = {
  name: string;
  color: IndustryColor;
  icon: IndustryIconKey;
};

const ROW_ONE: Industry[] = [
  { name: 'HVAC', color: 'blue', icon: 'hvac' },
  { name: 'Roofing', color: 'yellow', icon: 'roofing' },
  { name: 'Plumbing', color: 'brand', icon: 'plumbing' },
  { name: 'Pest Control', color: 'blue', icon: 'pest' },
  { name: 'Gutter Cleaning', color: 'yellow', icon: 'gutter' },
  { name: 'Painting', color: 'brand', icon: 'painting' },
];

const ROW_TWO: Industry[] = [
  { name: 'Carpet Cleaning', color: 'brand', icon: 'carpet' },
  { name: 'Lawn care', color: 'blue', icon: 'lawn' },
  { name: 'Pool Cleaning', color: 'yellow', icon: 'pool' },
  { name: 'Petcare', color: 'brand', icon: 'petcare' },
  { name: 'Moving and Packing', color: 'blue', icon: 'moving' },
  { name: 'Deck & Patio', color: 'yellow', icon: 'deck' },
];

const TRACK_COPIES = 2;

function IndustryIconGraphic({ icon }: { icon: IndustryIconKey }) {
  switch (icon) {
    case 'hvac':
      return <HvacIcon />;
    case 'roofing':
      return <RoofingIcon />;
    case 'plumbing':
      return <PlumbingIcon />;
    case 'pest':
      return <PestControlIcon />;
    case 'gutter':
      return <GutterCleaningIcon />;
    case 'painting':
      return <PaintingIcon />;
    case 'carpet':
      return <CarpetCleaningIcon />;
    case 'lawn':
      return <LawnCareIcon />;
    case 'pool':
      return <PoolCleaningIcon />;
    case 'petcare':
      return <PetcareIcon />;
    case 'moving':
      return <MovingIcon />;
    case 'deck':
      return <DeckPatioIcon />;
    default:
      return null;
  }
}

function IndustryItem({ industry }: { industry: Industry }) {
  return (
    <div className="industry_item">
      <div className={`industry_item_icon ${industry.color}`}>
        <IndustryIconGraphic icon={industry.icon} />
      </div>
      <h3 className="industry_item_text">{industry.name}</h3>
    </div>
  );
}

function IndustryTrack({
  industries,
  trackClass,
  trackRef,
}: {
  industries: Industry[];
  trackClass: string;
  trackRef: React.RefObject<HTMLDivElement>;
}) {
  const items = Array.from({ length: TRACK_COPIES }, () => industries).flat();

  return (
    <div className="industries_track_mover" ref={trackRef}>
      <div className={`industries_text_track ${trackClass}`}>
        {items.map((industry, index) => (
          <IndustryItem key={`${industry.name}-${index}`} industry={industry} />
        ))}
      </div>
    </div>
  );
}

export function IndustriesSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const trackOneRef = useRef<HTMLDivElement | null>(null);
  const trackTwoRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const getLoopWidth = (mover: HTMLDivElement) => {
      const track = mover.querySelector<HTMLDivElement>('.industries_text_track');
      return track ? track.scrollWidth / TRACK_COPIES : 0;
    };

    const updateTracks = () => {
      const section = sectionRef.current;
      const trackOne = trackOneRef.current;
      const trackTwo = trackTwoRef.current;
      if (!section || !trackOne || !trackTwo) return;

      const loopOne = getLoopWidth(trackOne);
      const loopTwo = getLoopWidth(trackTwo);
      if (loopOne <= 0 || loopTwo <= 0) return;

      const rect = section.getBoundingClientRect();
      const viewport = window.innerHeight;
      const range = viewport + rect.height;
      const scrolled = viewport - rect.top;
      const progress = Math.max(0, Math.min(1, scrolled / range));

      // 3 full loops while scrolling through the section (seamless via modulo)
      const travelOne = (progress * loopOne * 3) % loopOne;
      const travelTwo = (progress * loopTwo * 3) % loopTwo;

      trackOne.style.transform = `translate3d(${travelOne}px, 0, 0)`;
      trackTwo.style.transform = `translate3d(${-travelTwo}px, 0, 0)`;
    };

    updateTracks();

    const ro = new ResizeObserver(updateTracks);
    if (trackOneRef.current) ro.observe(trackOneRef.current);
    if (trackTwoRef.current) ro.observe(trackTwoRef.current);
    if (sectionRef.current) ro.observe(sectionRef.current);

    window.addEventListener('scroll', updateTracks, { passive: true });
    window.addEventListener('resize', updateTracks);

    return () => {
      ro.disconnect();
      window.removeEventListener('scroll', updateTracks);
      window.removeEventListener('resize', updateTracks);
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className="industries"
      aria-labelledby="industries-heading"
    >
      <div className="container">
        <h2 id="industries-heading" className="industries__title">
          Every home service supported
        </h2>

        <div className="industries__stage">
          <div className="industries__tracks-bg" aria-hidden="true">
            <div className="industries__track-row industries__track-row--top">
              <IndustryTrack
                industries={ROW_ONE}
                trackClass="one"
                trackRef={trackOneRef}
              />
            </div>
            <div className="industries__track-row industries__track-row--bottom">
              <IndustryTrack
                industries={ROW_TWO}
                trackClass="two"
                trackRef={trackTwoRef}
              />
            </div>
          </div>

          <div className="iphone-mockup">
            <div className="iphone14-screen">
              <img
                sizes="(max-width: 573px) 100vw, 573px"
                srcSet={IPHONE_SCREEN_SRCSET}
                src={IPHONE_SCREEN_SRC}
                alt="Review Lumen dashboard showing customer reviews"
                className="iphone14-image"
              />
            </div>
            <img
              loading="lazy"
              src={IPHONE_FRAME_SRC}
              alt=""
              className="iphone-shape-3"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
